import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import {
  PageContainer,
  Container,
  Section,
  PageTitle,
  SectionTitle,
  LeftAlignedContentText,
  LazyBackgroundImage,
  ImageContainer,
  ImageOverlay,
  ImageTitle,
  ImageDescription,
  BulletCardsGrid,
  BulletCard,
  BulletCardIcon,
  BulletCardText,
  CheckIcon,
  ItemGrid,
  ItemCardLight,
  ItemTitle,
  ItemBody,
  ExecutionCapabilityCard,
  ExecutionCapabilityNumber,
  StrengthCardDescription,
  CtaImageSection,
  PartnershipSection,
  PartnershipContent,
  SolutionsCtaTitle,
  SolutionsCtaButton,
  SectionDivider,
  staggerContainer,
  staggerItem,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
} from '../components/shared/SharedComponents';
import { useContent } from '../hooks/useContent';
import { useNavigationAwareAnimations } from '../hooks/useNavigationAwareAnimations';
import { preloadCriticalImages } from '../utils/imagePreloader';
import { PRELOAD_ABOUT_CRITICAL } from '../config/preloadConfig';

const ImageSection = styled.section`
  padding: 20px 0;
  background: transparent;
  
  @media (max-width: 768px) {
    padding: 20px 20px;
  }
`;


const ContentSection = styled(Section)`
  padding: 20px 0;
`;

const LeftAlignedSectionTitle = styled(SectionTitle)`
  text-align: left;
  margin-bottom: 12px;
`;

/** Institutional Continuity — compact 2×2 bullet cards (not full-width stack) */
const InstitutionalBulletsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: 720px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CompactInstitutionalBulletCard = styled(BulletCard)`
  padding: 14px 18px;
  gap: 12px;
  border-radius: 12px;
`;

const CompactInstitutionalIcon = styled(BulletCardIcon)`
  width: 28px;
  height: 28px;
  min-width: 28px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PromiseIntro = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: #1d334e;
  margin-bottom: 24px;
  line-height: 1.5;
`;

function About() {
  useNavigationAwareAnimations();
  const { about, footerInfo, contactInfo, site } = useContent();

  // Preload critical images (config is single source of truth)
  useEffect(() => {
    if (PRELOAD_ABOUT_CRITICAL.length > 0) {
      preloadCriticalImages(PRELOAD_ABOUT_CRITICAL);
    }
  }, []);

  return (
    <>
      <SEO
        title={about.title}
        description={about.sections && about.sections[0] ? about.sections[0].title : ''}
      />
      <StructuredData
        type="WebPage"
        data={{
          page: {
            title: about.title,
            description: about.sections && about.sections[0] ? about.sections[0].title : '',
            url: '/about'
          },
          organization: { footerInfo, contactInfo },
          site
        }}
        includeOrganization={true}
      />
      <PageContainer>
        <Container>
          <article>
            {/* Header */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={heroStaggerContainerVariants}
            >
              <PageTitle
                className="with-underline"
                variants={heroStaggerItemVariants}
              >
                {about.title}
              </PageTitle>
            </motion.div>

            {/* Intro Section */}
            {about.sections && about.sections[0] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[0].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[0].content && Array.isArray(about.sections[0].content) && about.sections[0].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>

                {about.sections[0].bullets && Array.isArray(about.sections[0].bullets) && (
                  <BulletCardsGrid style={{ marginTop: '24px', marginBottom: '24px' }}>
                    {about.sections[0].bullets.map((item, index) => (
                      <BulletCard key={index}>
                        <BulletCardIcon>
                          <CheckIcon />
                        </BulletCardIcon>
                        <BulletCardText>{item}</BulletCardText>
                      </BulletCard>
                    ))}
                  </BulletCardsGrid>
                )}

                {about.sections[0].contentAfter && Array.isArray(about.sections[0].contentAfter) && (
                  <LeftAlignedContentText
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {about.sections[0].contentAfter.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </LeftAlignedContentText>
                )}
              </ContentSection>
            )}
            {about.sections && about.sections[0] && <SectionDivider />}

            {/* Section 1: Institutional Continuity & Evolution */}
            {about.sections && about.sections[1] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[1].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[1].content && Array.isArray(about.sections[1].content) && about.sections[1].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>

                {about.sections[1].bullets && Array.isArray(about.sections[1].bullets) && (
                  <InstitutionalBulletsGrid>
                    {about.sections[1].bullets.map((item, index) => (
                      <CompactInstitutionalBulletCard key={index}>
                        <CompactInstitutionalIcon>
                          <CheckIcon />
                        </CompactInstitutionalIcon>
                        <BulletCardText>{item}</BulletCardText>
                      </CompactInstitutionalBulletCard>
                    ))}
                  </InstitutionalBulletsGrid>
                )}

                {about.sections[1].contentAfter && Array.isArray(about.sections[1].contentAfter) && (
                  <LeftAlignedContentText
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {about.sections[1].contentAfter.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </LeftAlignedContentText>
                )}
              </ContentSection>
            )}
            {about.sections && about.sections[1] && <SectionDivider />}

            {/* Image 0: Global Connection */}
            {about.images && about.images[0] && (
              <ImageSection>
                <figure>
                  <LazyBackgroundImage
                    src={about.images[0].src}
                    aria-label={about.images[0].title}
                  >
                    <ImageContainer
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ImageOverlay>
                        <ImageTitle>{about.images[0].title}</ImageTitle>
                        <ImageDescription>
                          {about.images[0].description}
                        </ImageDescription>
                      </ImageOverlay>
                    </ImageContainer>
                  </LazyBackgroundImage>
                  <figcaption style={{ display: 'none' }}>{about.images[0].title}: {about.images[0].description}</figcaption>
                </figure>
              </ImageSection>
            )}

            {/* Section 2: Our Mandate */}
            {about.sections && about.sections[2] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[2].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[2].content && Array.isArray(about.sections[2].content) && about.sections[2].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>

                {about.sections[2].bullets && Array.isArray(about.sections[2].bullets) && (
                  <BulletCardsGrid style={{ marginTop: '24px' }}>
                    {about.sections[2].bullets.map((item, index) => (
                      <BulletCard key={index}>
                        <BulletCardIcon>
                          <CheckIcon />
                        </BulletCardIcon>
                        <BulletCardText>{item}</BulletCardText>
                      </BulletCard>
                    ))}
                  </BulletCardsGrid>
                )}

                {about.sections[2].contentAfter && Array.isArray(about.sections[2].contentAfter) && (
                  <LeftAlignedContentText
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {about.sections[2].contentAfter.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </LeftAlignedContentText>
                )}
              </ContentSection>
            )}
            {about.sections && about.sections[2] && <SectionDivider />}


            {/* Image 1: Partnership */}
            {about.images && about.images[1] && (
              <ImageSection>
                <figure>
                  <LazyBackgroundImage
                    src={about.images[1].src}
                    aria-label={about.images[1].title}
                  >
                    <ImageContainer
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ImageOverlay>
                        <ImageTitle>{about.images[1].title}</ImageTitle>
                        <ImageDescription>
                          {about.images[1].description}
                        </ImageDescription>
                      </ImageOverlay>
                    </ImageContainer>
                  </LazyBackgroundImage>
                  <figcaption style={{ display: 'none' }}>{about.images[1].title}: {about.images[1].description}</figcaption>
                </figure>
              </ImageSection>
            )}

            {/* Section 3: Our Operating Architecture */}
            {about.sections && about.sections[3] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[3].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[3].content && Array.isArray(about.sections[3].content) && about.sections[3].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>

                {about.sections[3].points && Array.isArray(about.sections[3].points) && (
                  <ItemGrid style={{ marginTop: 32, marginBottom: 32 }}>
                    {about.sections[3].points.map((point, index) => (
                      <ExecutionCapabilityCard key={index}>
                        <ExecutionCapabilityNumber>{point.number}</ExecutionCapabilityNumber>
                        <ItemTitle style={{ marginBottom: 10 }}>{point.title}</ItemTitle>
                        <StrengthCardDescription>{point.description}</StrengthCardDescription>
                      </ExecutionCapabilityCard>
                    ))}
                  </ItemGrid>
                )}

                {about.sections[3].conclusion && Array.isArray(about.sections[3].conclusion) && (
                  <LeftAlignedContentText
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    viewport={{ once: true }}
                  >
                    {about.sections[3].conclusion.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </LeftAlignedContentText>
                )}
              </ContentSection>
            )}
            {about.sections && about.sections[3] && <SectionDivider />}



            {/* Image 2: Services & Team */}
            {about.images && about.images[2] && (
              <ImageSection>
                <figure>
                  <LazyBackgroundImage
                    src={about.images[2].src}
                    aria-label={about.images[2].title}
                  >
                    <ImageContainer
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ImageOverlay>
                        <ImageTitle>{about.images[2].title}</ImageTitle>
                        <ImageDescription>
                          {about.images[2].description}
                        </ImageDescription>
                      </ImageOverlay>
                    </ImageContainer>
                  </LazyBackgroundImage>
                  <figcaption style={{ display: 'none' }}>{about.images[2].title}: {about.images[2].description}</figcaption>
                </figure>
              </ImageSection>
            )}

            {/* Section 4: Decision Advantage & Responsible Growth */}
            {about.sections && about.sections[4] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[4].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[4].content && Array.isArray(about.sections[4].content) && about.sections[4].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>
              </ContentSection>
            )}
            {about.sections && about.sections[4] && <SectionDivider />}

            {/* Image 3: Integrity & Trust */}
            {about.images && about.images[3] && (
              <ImageSection>
                <figure>
                  <LazyBackgroundImage
                    src={about.images[3].src}
                    aria-label={about.images[3].title}
                  >
                    <ImageContainer
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ImageOverlay>
                        <ImageTitle>{about.images[3].title}</ImageTitle>
                        <ImageDescription>
                          {about.images[3].description}
                        </ImageDescription>
                      </ImageOverlay>
                    </ImageContainer>
                  </LazyBackgroundImage>
                  <figcaption style={{ display: 'none' }}>{about.images[3].title}: {about.images[3].description}</figcaption>
                </figure>
              </ImageSection>
            )}

            {/* Section 5: Our Core Principles */}
            {about.sections && about.sections[5] && about.sections[5].values && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[5].title}
                </LeftAlignedSectionTitle>

                <ItemGrid style={{ marginTop: 24 }}>
                  {Array.isArray(about.sections[5].values) && about.sections[5].values.map((value, index) => (
                    <ItemCardLight key={index}>
                      <ItemTitle style={{ marginBottom: 10 }}>{value.title}</ItemTitle>
                      <ItemBody>
                        <p>{value.description}</p>
                      </ItemBody>
                    </ItemCardLight>
                  ))}
                </ItemGrid>
              </ContentSection>
            )}
            {about.sections && about.sections[5] && about.sections[5].values && <SectionDivider />}

            {/* Final Image: Team & Office */}
            {about.images && about.images[4] && (
              <ImageSection>
                <figure>
                  <LazyBackgroundImage
                    src={about.images[4].src}
                    aria-label={about.images[4].title}
                  >
                    <ImageContainer
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                      viewport={{ once: true }}
                    >
                      <ImageOverlay>
                        <ImageTitle>{about.images[4].title}</ImageTitle>
                        <ImageDescription>
                          {about.images[4].description}
                        </ImageDescription>
                      </ImageOverlay>
                    </ImageContainer>
                  </LazyBackgroundImage>
                  <figcaption style={{ display: 'none' }}>{about.images[4].title}: {about.images[4].description}</figcaption>
                </figure>
              </ImageSection>
            )}

            {/* Section 6: Our Commitment */}
            {about.sections && about.sections[6] && (
              <ContentSection>
                <LeftAlignedSectionTitle
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[6].title}
                </LeftAlignedSectionTitle>

                <LeftAlignedContentText
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  viewport={{ once: true }}
                >
                  {about.sections[6].intro && (
                    <PromiseIntro>{about.sections[6].intro}</PromiseIntro>
                  )}
                  {about.sections[6].content && Array.isArray(about.sections[6].content) && about.sections[6].content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </LeftAlignedContentText>

                {about.sections[6].bullets && Array.isArray(about.sections[6].bullets) && (
                  <ItemGrid style={{ marginTop: 8 }}>
                    {about.sections[6].bullets.map((item, index) => (
                      <ItemCardLight key={index}>
                        <ItemBody>
                          <p style={{ margin: 0 }}>{item}</p>
                        </ItemBody>
                      </ItemCardLight>
                    ))}
                  </ItemGrid>
                )}

                {about.sections[6].conclusion && (
                  <LeftAlignedContentText><p>{about.sections[6].conclusion}</p></LeftAlignedContentText>
                )}
              </ContentSection>
            )}
            {about.sections && about.sections[6] && <SectionDivider />}

            {about.sections?.[6] && (about.sections[6].bridge || about.sections[6].cta) && (
              <CtaImageSection src="/ui/handshake-people.png" overlay={0.45}>
                <PartnershipSection
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  <PartnershipContent>
                    {about.sections[6].bridge && (
                      <SolutionsCtaTitle as={motion.h3} variants={staggerItem}>
                        {about.sections[6].bridge}
                      </SolutionsCtaTitle>
                    )}
                    {about.sections[6].cta?.text && (
                      <SolutionsCtaButton to={about.sections[6].cta.link || '/contact'}>
                        {about.sections[6].cta.text}
                      </SolutionsCtaButton>
                    )}
                  </PartnershipContent>
                </PartnershipSection>
              </CtaImageSection>
            )}
          </article>
        </Container>
      </PageContainer>
    </>
  );
}

export default About;
